import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Popover,
  Button,
  Typography,
} from '@mui/material';
import { Clear, Close } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { filterInputSx } from '../common/filterStyles';

export interface EBFilterValues {
  document_id: string;
  verified: string;
  extraction_id: string;
  training_set_label: string;
  created_at: string;
  validation_errors: string;
  training_set_certified: string;
}

export const emptyEBFilters: EBFilterValues = {
  document_id: '',
  verified: '',
  extraction_id: '',
  training_set_label: '',
  created_at: '',
  validation_errors: '',
  training_set_certified: '',
};

export const EB_FILTER_KEYS = Object.keys(emptyEBFilters) as (keyof EBFilterValues)[];

const TEXT_FIELDS: { key: keyof EBFilterValues; placeholder: string }[] = [
  { key: 'document_id', placeholder: 'Document ID' },
  { key: 'extraction_id', placeholder: 'Extraction ID' },
];

interface ExtractionBatchFiltersPopoverProps {
  anchorEl: HTMLElement | null;
  currentValues: EBFilterValues;
  onClose: () => void;
  onApply: (values: EBFilterValues) => void;
}

const ExtractionBatchFiltersPopover = ({
  anchorEl,
  currentValues,
  onClose,
  onApply,
}: ExtractionBatchFiltersPopoverProps) => {
  const [draft, setDraft] = useState<EBFilterValues>(currentValues);
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    const isOpen = Boolean(anchorEl);
    if (isOpen && !wasOpenRef.current) {
      setDraft(currentValues);
    }
    wasOpenRef.current = isOpen;
  }, [anchorEl, currentValues]);

  const handleReset = () => setDraft(emptyEBFilters);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '8px', p: 2, width: 420 } } }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onApply(draft);
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {/* Has Validation Errors */}
          <TextField
            select
            size="small"
            label="Validation Errors"
            variant="outlined"
            value={draft.validation_errors || ''}
            onChange={(e) => setDraft((prev) => ({ ...prev, validation_errors: e.target.value }))}
            data-cy="filter-validation-errors"
            sx={filterInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>

          {/* Training Set Certified */}
          <TextField
            select
            size="small"
            label="Certified"
            variant="outlined"
            value={draft.training_set_certified || ''}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, training_set_certified: e.target.value }))
            }
            data-cy="filter-training-set-certified"
            sx={filterInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>

          {/* Has Been Sent */}
          <TextField
            select
            size="small"
            label="Has Been Sent"
            variant="outlined"
            value={draft.verified || ''}
            onChange={(e) => setDraft((prev) => ({ ...prev, verified: e.target.value }))}
            data-cy="filter-verified"
            sx={filterInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>

          {/* Created Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Created Date"
              value={draft.created_at ? dayjs(draft.created_at) : null}
              onChange={(date) =>
                setDraft((prev) => ({
                  ...prev,
                  created_at: date && date.isValid() ? date.format('YYYY-MM-DD') : '',
                }))
              }
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  sx: filterInputSx,
                  inputProps: { 'data-cy': 'filter-created-date' },
                },
                field: {
                  clearable: true,
                  onClear: () => setDraft((prev) => ({ ...prev, created_at: '' })),
                },
              }}
            />
          </LocalizationProvider>

          {/* Training Set Label */}
          <TextField
            size="small"
            placeholder="Training Set Label"
            label="Training Set Label"
            variant="outlined"
            autoComplete="off"
            value={draft.training_set_label}
            onChange={(e) => setDraft((prev) => ({ ...prev, training_set_label: e.target.value }))}
            data-cy="filter-training_set_label"
            sx={filterInputSx}
            InputProps={{
              endAdornment: draft.training_set_label && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setDraft((prev) => ({ ...prev, training_set_label: '' }))}
                    edge="end"
                    sx={{ mr: -1, p: 0.25 }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Text fields */}
          {TEXT_FIELDS.map(({ key, placeholder }) => (
            <TextField
              key={key}
              size="small"
              placeholder={placeholder}
              label={placeholder}
              variant="outlined"
              autoComplete="off"
              value={draft[key]}
              onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
              data-cy={`filter-${key}`}
              sx={filterInputSx}
              InputProps={{
                endAdornment: draft[key] && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setDraft((prev) => ({ ...prev, [key]: '' }))}
                      edge="end"
                      sx={{ mr: -1, p: 0.25 }}
                    >
                      <Clear sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
          <Button
            size="small"
            type="button"
            onClick={handleReset}
            data-cy="btn-reset-filters"
            sx={{
              textTransform: 'none',
              fontSize: '12.5px',
              fontWeight: 500,
              height: 34,
              borderRadius: '6px',
              color: '#5f6d7e',
              border: '1px solid rgba(0,0,0,0.12)',
              '&:hover': { borderColor: 'rgba(0,0,0,0.24)', backgroundColor: 'rgba(0,0,0,0.02)' },
            }}
          >
            Reset
          </Button>
          <Button
            size="small"
            type="submit"
            variant="contained"
            data-cy="btn-apply-filters"
            sx={{
              textTransform: 'none',
              fontSize: '12.5px',
              fontWeight: 500,
              height: 34,
              borderRadius: '6px',
              backgroundColor: '#1D9AAA',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#178a99', boxShadow: 'none' },
            }}
          >
            Apply
          </Button>
        </Box>
      </form>
    </Popover>
  );
};

export default ExtractionBatchFiltersPopover;
